<script setup lang="ts">
/**
 * The question asked before something cannot be taken back (SPEC §9.1, MT-113).
 *
 * The screens that had confirmations each wrote their own dialog, and the
 * screens that did not have one simply acted on the click — removing a
 * co-organizer, handing a group to somebody else, withdrawing an invitation.
 * Ownership transfer is the one that matters: an organizer who mis-taps it
 * cannot undo it themselves, because they are no longer the owner.
 *
 * Two deliberate choices in here:
 *
 * **The buttons say what they do.** "Yes"/"No" forces the person to have read
 * the sentence above to know which is which, and the whole point of the pause
 * is that they might not have. "Remove Ellen" and "Keep Ellen" are readable
 * alone.
 *
 * **Cancel is the default focus.** Somebody hammering Enter after a mis-click
 * should land on the harmless one.
 */
withDefaults(
  defineProps<{
    /** Whether the dialog is showing. */
    modelValue: boolean
    /** The question, in plain language. */
    title: string
    /** What will actually happen, including anything irreversible. */
    body?: string
    /** The label on the button that proceeds. Should name the action. */
    confirmLabel: string
    /** The label on the button that does not. Should name the status quo. */
    cancelLabel?: string
    /** Colours the confirm button as a warning rather than a primary action. */
    destructive?: boolean
    /** Disables both buttons while the action is in flight. */
    busy?: boolean
  }>(),
  { body: undefined, cancelLabel: 'Cancel', destructive: false, busy: false },
)

defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
}>()
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="24rem"
    :persistent="busy"
    data-testid="confirm-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title tag="h2" class="text-h6">{{ title }}</v-card-title>

      <v-card-text v-if="body" class="text-body-2">{{ body }}</v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          variant="text"
          autofocus
          :disabled="busy"
          data-testid="confirm-cancel"
          @click="$emit('update:modelValue', false)"
        >
          {{ cancelLabel }}
        </v-btn>

        <v-btn
          variant="flat"
          :color="destructive ? 'error' : 'primary'"
          :loading="busy"
          data-testid="confirm-accept"
          @click="$emit('confirm')"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
